/**
 * Contexte i18n léger pour la boutique publique.
 *
 * - Charge dynamiquement les dictionnaires fr/de/it depuis ./i18n/{lang}.json
 * - Persiste le choix dans localStorage ("maison-thai-lang")
 * - Lit `?lang=de` (ou de|it|fr) de l'URL en priorité
 * - Détection auto au premier visit via navigator.language
 *
 * Usage :
 *   const { t, lang, setLang } = useI18n();
 *   t("hero.title_1")
 *   t("cart.isotherm_out_of_range", { count: 2, plural: "s" })
 */
import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import frDict from "./i18n/fr.json";
import deDict from "./i18n/de.json";
import itDict from "./i18n/it.json";

const DICTS = { FR: frDict, DE: deDict, IT: itDict };
const SUPPORTED = ["FR", "DE", "IT"];
const STORAGE_KEY = "maison-thai-lang";

function detectInitialLang() {
  if (typeof window === "undefined") return "FR";
  try {
    // 1) URL ?lang=de
    const urlLang = new URLSearchParams(window.location.search).get("lang");
    if (urlLang) {
      const up = urlLang.toUpperCase();
      if (SUPPORTED.includes(up)) return up;
    }
    // 2) localStorage
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && SUPPORTED.includes(stored)) return stored;
    // 3) navigator.language
    const nav = (window.navigator.language || "fr").slice(0, 2).toUpperCase();
    if (SUPPORTED.includes(nav)) return nav;
  } catch (_e) {
    /* ignore */
  }
  return "FR";
}

function resolveKey(dict, key) {
  if (!key) return "";
  const parts = key.split(".");
  let cur = dict;
  for (const p of parts) {
    if (cur && typeof cur === "object" && p in cur) cur = cur[p];
    else return undefined;
  }
  return typeof cur === "string" ? cur : undefined;
}

function interpolate(template, params) {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (_m, k) => (params[k] !== undefined ? String(params[k]) : `{${k}}`));
}

const I18nContext = createContext({ lang: "FR", setLang: () => {}, t: (k) => k });

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(detectInitialLang);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, lang);
      document.documentElement.setAttribute("lang", lang.toLowerCase());
    } catch (_e) {
      /* ignore */
    }
  }, [lang]);

  const setLang = useCallback((next) => {
    const up = (next || "FR").toUpperCase();
    if (SUPPORTED.includes(up)) setLangState(up);
  }, []);

  const t = useCallback(
    (key, params) => {
      const primary = resolveKey(DICTS[lang], key);
      if (primary !== undefined) return interpolate(primary, params);
      // Fallback FR si traduction manquante
      const fallback = resolveKey(DICTS.FR, key);
      if (fallback !== undefined) return interpolate(fallback, params);
      // Dernier recours : la clé elle-même
      return key;
    },
    [lang],
  );

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}

// Helper "p" pour pluriels simples FR/DE/IT (s/en/e)
export function plural(count, lang) {
  if (count === 1) return "";
  if (lang === "DE") return "n";
  return "s"; // FR + IT utilisent "s" pour la plupart des cas (vaschette = vaschetta+e gérée au cas par cas)
}
